import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { CalendarCheck, CalendarClock, CalendarPlus, Ellipsis, Mail, MessageSquare, Phone } from "lucide-react";
import { contactPolicy } from "@shared/contactPolicy";
import { pickableLeadStatus } from "@shared/schemas/leads";
import { errorMessage } from "@web/lib/api";
import { fmtDate, fullName, statusLabel, statusTone } from "@web/lib/format";
import { LEAD_STATUS_LABEL, PICKABLE_LEAD_STATUSES, leadStatusLabel } from "@web/lib/formatLeads";
import { leadApi, leadQuery, type LeadDetail as Lead, type Tour } from "@web/lib/queries-leads";
import { ActionBar, BottomSheet, Button, Card, ContactSummary, EmptyState, Inline, LeadCard, Page, Picker, Row, RowGroup, SegmentedControl, Skeleton, Stack, StatusChip, Text, Workspace, Region, useToast } from "@web/ui";
import { CallSheet, EmailSheet, TextSheet, type CommsTarget } from "./lead/CommsSheets";
import { ConvertSheet } from "./lead/ConvertSheet";
import { DetailsTab } from "./lead/DetailsTab";
import { MoreSheet, type MoreMode } from "./lead/MoreSheets";
import { TimelineTab } from "./lead/TimelineTab";
import { TourActionsSheet, TourSheet } from "./lead/TourSheets";
import { ContactSheet } from "./sheets/ContactSheet";
import { ContactPicker } from "./sheets/ContactPicker";
import type { Contact } from "@web/lib/queries";
import { recordValidationQuery } from "@web/lib/validation";
import { RetainedPanel, UnsavedChanges, ValidationSummary, focusValidationField } from "@web/ui";

const TABS = [
  { key: "timeline", label: "Timeline" },
  { key: "details", label: "Details" },
] as const;
type TabKey = (typeof TABS)[number]["key"];
type Sheet = "text" | "call" | "email" | "tour" | "convert" | null;

export const targetOf = (l: Lead): CommsTarget => ({ ...l.contact, leadId: l.id, contactId: l.contactId, name: fullName(l.contact) || "client" });

/** Where this lead is in a sales sequence (SPEC §6.1 item 4, §7.10). A member
 * about to text somebody should be able to see that a scripted message is queued
 * behind theirs — and that sending one pauses the sequence for three days.
 * Pausing and cancelling by hand is Phase 6; this is the status. */
function DripChip({ drip }: { drip: NonNullable<Lead["drip"]> }) {
  const tone = drip.status === "active" ? "accent" : drip.status === "paused" ? "warn" : "neutral";
  const detail = drip.status === "active" ? (drip.nextAt ? `next on ${fmtDate(drip.nextAt.slice(0, 10))}` : "next message pending")
    : drip.status === "paused" ? (drip.pausedUntil ? `paused until ${fmtDate(drip.pausedUntil.slice(0, 10))}` : "paused")
      : drip.status === "cancelled" ? `stopped${drip.cancelReason ? ` · ${drip.cancelReason.replaceAll("_", " ")}` : ""}`
        : drip.status === "transitioned" ? "moved to the next sequence" : "finished";
  return <Inline gap={2} wrap>
    <StatusChip tone={tone}>{drip.sequenceName}</StatusChip>
    <Text variant="caption" tone="secondary">Step {Math.min(drip.step, drip.steps || drip.step)} of {drip.steps || drip.step} · {detail}</Text>
  </Inline>;
}

/** Lead detail (SPEC §6.1 item 4): header · status picker · Timeline / Details · docked action bar. */
export function LeadDetail() {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const toast = useToast();
  const q = useQuery(leadQuery(id));
  const validation = useQuery(recordValidationQuery("lead", id));
  const [tab, setTab] = useState<TabKey>("timeline");
  const [sheet, setSheet] = useState<Sheet>(null);
  const [more, setMore] = useState<MoreMode | null>(null);
  const [tour, setTour] = useState<Tour | null>(null);
  const [contactId, setContactId] = useState<string | null>(null);
  const [pickingContact, setPickingContact] = useState(false);
  const contactSaving = useRef(false);
  const [contactBusy, setContactBusy] = useState(false);
  const [contactText, setContactText] = useState<CommsTarget | null>(null);
  const l = q.data;

  async function setSecondary(contact: Contact | null) {
    if (!l) return;
    // ContactPicker owns the in-flight child lock and keeps the selected/saved
    // contact on rejection. Do not close or swallow an attachment failure.
    await leadApi.patchLead(l.id, { secondaryContactId: contact?.id ?? null });
    setPickingContact(false);
    toast.success(contact ? "Second contact added" : "Second contact removed");
  }

  async function removeSecondary() {
    if (contactSaving.current) return;
    contactSaving.current = true; setContactBusy(true);
    try { await setSecondary(null); }
    catch (e) { toast.error(errorMessage(e)); }
    finally { contactSaving.current = false; setContactBusy(false); }
  }

  async function changeStatus(status: string) {
    if (contactSaving.current || !l || status === l.status) return;
    if (status === "lost") return setMore("lost");
    if (status === "dnc") return setMore("dnc");
    try {
      await leadApi.setStatus(l.id, pickableLeadStatus.parse(status));
      toast.success(`Now ${leadStatusLabel(status)}`);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }

  if (q.isError)
    return (
      <Page>
        <EmptyState title="Lead not found" body={errorMessage(q.error)} action={<Button fullWidth={false} variant="secondary" onClick={() => navigate({ to: "/leads" })}>Back to leads</Button>} />
      </Page>
    );
  if (!l)
    return (
      <Page>
        <Skeleton variant="cards" count={3} />
      </Page>
    );

  const target = targetOf(l);
  const sms = contactPolicy(target, "sms"), email = contactPolicy(target, "email"), call = contactPolicy(target, "call");
  const converted = Boolean(l.bookingId);
  const statusOptions = [...PICKABLE_LEAD_STATUSES.map((s) => ({ value: s, label: LEAD_STATUS_LABEL[s] })), ...(PICKABLE_LEAD_STATUSES.includes(l.status as never) ? [] : [{ value: l.status, label: leadStatusLabel(l.status), disabled: true }])];

  return (
    <Page>
      <UnsavedChanges dirty={false} pending={contactBusy} />
      <Workspace variant="record">
      <Region area="aside"><Stack gap={4}>
        <Card>
          <Stack gap={3}>
            <LeadCard identity card={l.card} status={l.bookingStatus ?? l.status} onClick={() => setContactId(l.contactId)} />
            {l.drip && <DripChip drip={l.drip} />}
            <Picker label="Status" value={l.status} onChange={changeStatus} disabled={converted || contactBusy} options={statusOptions} hint={converted ? "Converted leads follow their booking" : undefined} />
          </Stack>
        </Card>
        <Card>
          <Text variant="headline">Contacts</Text>
          <ContactSummary contact={l.contact} role="Primary" onOpen={() => setContactId(l.contactId)} onText={() => setSheet("text")} />
          {l.secondaryContact && <ContactSummary contact={l.secondaryContact} role="Secondary" onOpen={() => setContactId(l.secondaryContact!.id)} onText={() => setContactText({ ...l.secondaryContact!, leadId: l.id, contactId: l.secondaryContact!.id, name: fullName(l.secondaryContact!) })} />}
          <Stack gap={2}>
            <Button variant="secondary" disabled={contactBusy} onClick={() => { if (!contactSaving.current) setPickingContact(true); }}>{l.secondaryContact ? "Change second contact" : "+ Add second contact"}</Button>
            {l.secondaryContact && <Button variant="secondary" disabled={contactBusy} onClick={removeSecondary}>Remove second contact</Button>}
          </Stack>
        </Card>
        {converted && l.booking && (
          <RowGroup>
            <Row icon={<CalendarCheck aria-hidden size={22} />} title="Booking" subtitle={`${fmtDate(l.booking.eventDate, { weekday: true })}${l.booking.eventName ? ` · ${l.booking.eventName}` : ""}`} trailing={<StatusChip tone={statusTone(l.booking.status)}>{statusLabel(l.booking.status)}</StatusChip>} onClick={() => navigate({ to: "/bookings/$id", params: { id: l.booking!.id } })} />
          </RowGroup>
        )}
        {l.status === "lost" && l.lostReason && (
          <Text variant="caption" tone="danger" align="center">
            Lost — {l.lostReason}
          </Text>
        )}
      </Stack></Region>
      <Region area="main"><Stack gap={4}>
        <ValidationSummary result={validation.data} saved loading={validation.isFetching} unavailable={validation.isError} onRetry={() => void validation.refetch()} onIssue={issue => { if (issue.entity.type === "contact" && issue.entity.id) setContactId(issue.entity.id); else { setTab("details"); requestAnimationFrame(() => focusValidationField(issue)); } }} />
        <SegmentedControl items={TABS} value={tab} onChange={setTab} />
        <RetainedPanel active={tab === "timeline"} label="Lead timeline"><TimelineTab key={l.id} lead={l} onTour={setTour} /></RetainedPanel>
        <RetainedPanel active={tab === "details"} label="Lead details"><DetailsTab key={l.id} lead={l} onTour={setTour} onContact={setContactId} /></RetainedPanel>
      </Stack></Region>
      <Region area="actions">
      <ActionBar
        items={[
          { key: "text", label: "Text", icon: <MessageSquare aria-hidden size={22} />, disabled: !sms.allowed, disabledReason: sms.reason, onClick: () => setSheet("text") },
          { key: "call", label: "Call", icon: <Phone aria-hidden size={22} />, disabled: !call.allowed, disabledReason: call.reason, onClick: () => setSheet("call") },
          { key: "email", label: "Email", icon: <Mail aria-hidden size={22} />, disabled: !email.allowed, disabledReason: email.reason, onClick: () => setSheet("email") },
          { key: "tour", label: "Tour", icon: <CalendarClock aria-hidden size={22} />, onClick: () => setSheet("tour") },
          ...(converted ? [] : [{ key: "book", label: "Book", icon: <CalendarPlus aria-hidden size={22} />, onClick: () => setSheet("convert") }]),
          { key: "more", label: "More", icon: <Ellipsis aria-hidden size={22} />, onClick: () => setMore("menu") },
        ]}
      />
      </Region>
      </Workspace>
      <ContactSheet contactId={contactId} onClose={() => setContactId(null)} />
      <BottomSheet open={pickingContact} onClose={() => { if (!contactSaving.current) setPickingContact(false); }} title="Second contact"><ContactPicker value={null} onPendingChange={pending => { contactSaving.current = pending; setContactBusy(pending); }} onChange={(contact) => { if (contact) return setSecondary(contact); }} /></BottomSheet>
      {contactText && <TextSheet target={contactText} onClose={() => setContactText(null)} />}
      {sheet === "text" && <TextSheet target={target} onClose={() => setSheet(null)} />}
      {sheet === "call" && <CallSheet target={target} onClose={() => setSheet(null)} />}
      {sheet === "email" && <EmailSheet target={target} onClose={() => setSheet(null)} />}
      {sheet === "tour" && <TourSheet lead={l} onClose={() => setSheet(null)} />}
      {sheet === "convert" && <ConvertSheet lead={l} onClose={() => setSheet(null)} />}
      {more && <MoreSheet lead={l} mode={more} onClose={() => setMore(null)} onMode={setMore} />}
      {/* Stays mounted on local state while its flow runs (completing refetches the lead and the tour stops being "scheduled"). */}
      {tour && <TourActionsSheet lead={l} tour={l.tours.find((t) => t.id === tour.id) ?? tour} onClose={() => setTour(null)} />}
    </Page>
  );
}
